import { NavLink, Outlet } from "react-router-dom";
import { AppHeader } from "./app-header";
import { flatNavItems, mobileNavLabel, NAV_GROUPS } from "./app-nav-model";

// Sidebar rows, NavioWorks AppSidebar style: compact, muted by default, active = soft muted fill.
const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-8 items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
    isActive ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
  `flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
    isActive ? "bg-muted text-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
  }`;

// Desktop sidebar — narrow, grouped, no brand/sign-out (those live in the header now).
function Sidebar() {
  return (
    <aside className="hidden w-44 shrink-0 flex-col overflow-y-auto border-r border-border bg-background p-2 md:flex">
      {NAV_GROUPS.map((group) => (
        <nav key={group.label} className="mb-3 flex flex-col gap-1" aria-label={group.label}>
          <p className="mb-0.5 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{group.label}</p>
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                <Icon size={14} aria-hidden="true" className="shrink-0" />
                <span className="min-w-0 truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      ))}
    </aside>
  );
}

// Mobile primary nav — a horizontal scroll strip under the header (the sidebar is hidden below md).
function MobileNav() {
  return (
    <nav className="nav-scroll flex items-center gap-1 overflow-x-auto border-b border-border bg-background px-2 py-1.5 md:hidden" aria-label="Primary">
      {flatNavItems().map((item) => {
        const Icon = item.icon;
        return (
          <NavLink key={item.to} to={item.to} end={item.end} className={mobileLinkClass}>
            <Icon size={14} aria-hidden="true" />
            <span>{mobileNavLabel(item)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

// The authenticated shell: top header + (narrow sidebar | routed content), aligned with NavioWorks' AppLayout.
export function AppShell() {
  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-background text-foreground">
      <AppHeader />
      <MobileNav />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Sidebar />
        <main className="min-w-0 flex-1 overflow-auto bg-muted/30 p-4 sm:p-5 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
